import { Button } from '@heidi/design-system/button';
import { Skeleton } from '@heidi/design-system/skeleton';
import { TypographyP2, typographyVariant } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { Check } from 'lucide-react';
import { useIntl } from 'react-intl';

import { objectWorkspaceMessages as messages } from './ObjectWorkspace.messages';
import type {
  ObjectWorkspaceTask,
  ObjectWorkspaceTasksState,
} from './objectWorkspace.data';

interface ObjectWorkspaceWorkContentProps {
  state: ObjectWorkspaceTasksState;
  selectedTaskIds: readonly string[];
  onTaskToggle?: (task: ObjectWorkspaceTask) => void;
}

function TaskRowsLoading(): JSX.Element {
  const intl = useIntl();

  return (
    <div
      aria-busy="true"
      aria-label={intl.formatMessage(messages.loadingTasks)}
      className="flex flex-col gap-1"
      role="status"
    >
      {[0, 1, 2].map((row) => (
        <div key={row} className="flex flex-col gap-1.5 px-3 py-2.5">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/3" />
        </div>
      ))}
    </div>
  );
}

function TaskRow({
  task,
  selected,
  onToggle,
}: {
  task: ObjectWorkspaceTask;
  selected: boolean;
  onToggle?: (task: ObjectWorkspaceTask) => void;
}): JSX.Element {
  const intl = useIntl();
  const linkedProjectId = task.launch.workProjectId?.trim();
  const meta = [
    task.inProgress ? intl.formatMessage(messages.taskInProgress) : null,
    task.dueDate
      ? intl.formatMessage(messages.taskDue, {
          date: intl.formatDate(task.dueDate, { day: 'numeric', month: 'short' }),
        })
      : null,
    task.detail ?? null,
  ].filter((part): part is string => Boolean(part));

  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      {...(linkedProjectId
        ? {
            'aria-label': intl.formatMessage(messages.openLinkedTaskChat, {
              title: task.title,
            }),
          }
        : { 'aria-pressed': selected })}
      className={cn(
        'h-auto w-full justify-start gap-3 whitespace-normal rounded-lg px-3 py-2.5 text-start',
        selected &&
          !linkedProjectId &&
          'bg-surface-selected hover:bg-surface-selected-hover'
      )}
      onClick={() => onToggle?.(task)}
    >
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span
          className={cn(
            typographyVariant({ variant: 'p2' }),
            'line-clamp-2 text-text-primary'
          )}
        >
          {task.title}
        </span>
        {meta.length > 0 ? (
          <span
            className={cn(
              typographyVariant({ variant: 'caption' }),
              'line-clamp-1 text-text-tertiary'
            )}
          >
            {meta.join(' · ')}
          </span>
        ) : null}
      </span>
      {selected ? (
        <Check className="size-4 shrink-0 text-text-primary" aria-hidden />
      ) : null}
    </Button>
  );
}

export function ObjectWorkspaceWorkContent({
  state,
  selectedTaskIds,
  onTaskToggle,
}: ObjectWorkspaceWorkContentProps): JSX.Element {
  const intl = useIntl();

  let content: JSX.Element;
  if (state.status === 'loading') {
    content = <TaskRowsLoading />;
  } else if (state.status === 'error') {
    content = (
      <TypographyP2 className="text-text-secondary">
        {intl.formatMessage(messages.tasksUnavailable)}
      </TypographyP2>
    );
  } else if (state.tasks.length === 0) {
    content = (
      <TypographyP2 className="text-text-secondary">
        {intl.formatMessage(messages.noTasks)}
      </TypographyP2>
    );
  } else {
    content = (
      <div className="flex flex-col gap-1">
        {state.tasks.map((task) => (
          <TaskRow
            key={task.id}
            task={task}
            selected={selectedTaskIds.includes(task.id)}
            onToggle={onTaskToggle}
          />
        ))}
      </div>
    );
  }

  return (
    <section
      aria-label={intl.formatMessage(messages.workHeading)}
      className="flex flex-col"
    >
      {content}
    </section>
  );
}
